import { Box, BoxProps, CheckBox, DateInput, Select, Spinner, Text } from 'grommet';
import { Yoga } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { SessionActivityRow } from '.';
import { AddNewButton, Delete, TalonTip } from '../..';

import { RadioTypes, sessionEstimateItemTitles, WorkTimeType } from '@shared/types';
import {
  Commercial,
  CommercialType,
  Location,
  Role,
  Session,
  SessionActivity,
  SessionActivityType,
  SessionEstimateItemUnitType,
  TUpdateSessionRequest,
} from '@talon/web/lib/api';
import {
  useCommercialStore,
  useEstimateStore,
  useProjectStore,
  useRoleStore,
  useSessionActivityStore,
  useSessionStore,
} from '@talon/web/src/context';
import { USD } from '@talon/web/src/utils/currency';
import dayjs from 'dayjs';
import { borderRadiusSize } from 'src/theme';

export interface ISessionRowProps {
  sessionId: string;
  onChecked: () => void;
  onDelete: () => Promise<void>;
  checked: boolean;
  locations: Location[];
  border?: BoxProps['border'];
  rowIndex?: number;
  onChange?: () => void;
  onSaveComplete?: () => void;
}

export const SessionRow = observer(
  ({
    sessionId,
    locations,
    onChange,
    onSaveComplete,
    onDelete,
  }: ISessionRowProps) => {
    /** Refs **/

    /** Context **/
    const commercialStore = useCommercialStore();
    const projectStore = useProjectStore();
    const sessionStore = useSessionStore();
    const estimateStore = useEstimateStore();
    const roleStore = useRoleStore();
    const sessionActivityStore = useSessionActivityStore();

    /** State **/
    const [session, setSession] = useState<Session | undefined>(undefined);
    const [roles, setRoles] = useState<Role[]>([]);
    const [commercials, setCommercials] = useState<Commercial[]>([]);
    const [straightHours, setStraightHours] = useState(0);
    const [overtimeHours, setOvertimeHours] = useState(0);

    /** Computed **/
    const tempEstimate = estimateStore.tempEstimate;
    const sessionEstimate = estimateStore.getTempSessionEstimate(session?.id);

    /** Methods **/

    const recalculateSessionEstimate = async () => {
      if (!session) return;

      const sessionEstimate = tempEstimate?.session_estimates.find(
        (sessionEstimate) => sessionEstimate.session?.id === session.id
      );

      if (sessionEstimate) {
        await estimateStore.updateSessionEstimate(sessionEstimate.id, undefined, undefined, undefined, true);
      }
      onSaveComplete?.();
    };

    /**
     * Update Session
     */
    const updateSession = async (update: TUpdateSessionRequest) => {
      if (!session) {
        console.log('no session');
        return;
      }
      onChange?.();
      const updatedSession = await sessionStore.updateSession(session.id, update);

      if (updatedSession) {
        setSession(updatedSession);

        // Refresh the temp estimate
        await recalculateSessionEstimate();
      }
    };

    /**
     * Add Session Activity
     */
    const addSessionActivity = async () => {
      if (!session) return;
      onChange?.();
      const previousActivity: SessionActivity | undefined = session.activities[session.activities.length - 1];
      const startTime = dayjs(previousActivity?.end_time ?? `${session.date}T09:00:00`).toISOString();
      const endTime = previousActivity
        ? dayjs(previousActivity.end_time).add(1, 'hour').toISOString()
        : new Date(`${session.date}T17:00:00`).toISOString();
      const sessionActivity = await sessionActivityStore.createSessionActivity(
        session.id,
        SessionActivityType.WorkTime,
        startTime,
        endTime,
        session.script?.id
      );
      if (sessionActivity) {
        const activities = [...session.activities, sessionActivity];
        const updatedSession = { ...session, activities };

        const sessionEstimates = (await sessionStore.getSession(session.id, 'session_estimates'))?.session_estimates;
        if (sessionEstimates) updatedSession.session_estimates = sessionEstimates;

        setSession(updatedSession);

        // Refresh the temp estimate
        await recalculateSessionEstimate();
      }
    };

    /**
     * Update Session Activity
     */
    const updateSessionActivity = async (update: SessionActivity) => {
      if (!session) return;
      onChange?.();
      const sa = await sessionActivityStore.updateSessionActivity(update);
      if (!sa) return;

      const activities = session.activities?.map((a) => (a.id === sa.id ? sa : a));
      const updatedSession = { ...session, activities };

      setSession(updatedSession);

      // Refresh the temp estimate
      await recalculateSessionEstimate();
    };

    /**
     * Delete Session Activity
     */
    const deleteSessionActivity = async (activityId: string) => {
      if (!session) return;
      onChange?.();
      await sessionActivityStore.deleteSessionActivity(activityId);

      const activities = session?.activities?.filter((a) => a.id !== activityId);
      const updatedSession = { ...session, activities };

      setSession(updatedSession);

      // Refresh the temp estimate
      await recalculateSessionEstimate();
    };

    /** Effects **/

    useEffect(() => {
      const getSession = async () => {
        const s = await sessionStore.getSession(sessionId);
        s?.activities?.sort((a, b) => {
          return dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? -1 : 1;
        });
        s && setSession(s);
      };
      getSession();
    }, [sessionId]);

    useEffect(() => {
      const getDropDownOptions = async () => {
        if (!projectStore.currentProject) return;
        if (!session) return;
        const [r, c] = await Promise.all([
          roleStore.getRolesByProjectId(projectStore.currentProject.id),
          commercialStore.listCommercialsByProjectId(projectStore.currentProject.id),
        ]);
        setRoles(
          r?.filter((role) =>
            session.commercial_type === CommercialType.Radio
              ? RadioTypes.includes(role.type)
              : session.commercial_type === CommercialType.TV
                ? !RadioTypes.includes(role.type)
                : true
          ) || []
        );
        setCommercials(c || []);
      };
      getDropDownOptions();
    }, [projectStore.currentProject?.id, session?.activities]);

    useEffect(() => {
      if (sessionEstimate) {
        let st = 0;
        let ot = 0;

        for (const item of sessionEstimate.items) {
          if (item.unit_type === SessionEstimateItemUnitType.Hours) {
            const title = item.title.toLocaleLowerCase();
            if (title.includes(sessionEstimateItemTitles[WorkTimeType.StraightTime].toLocaleLowerCase())) {
              st += item.units ?? 0;
            } else if (
              title.includes(sessionEstimateItemTitles[WorkTimeType.TimeAndOneHalf].toLocaleLowerCase()) ||
              title.includes(sessionEstimateItemTitles[WorkTimeType.DoubleTime].toLocaleLowerCase())
            ) {
              ot += item.units ?? 0;
            }
          }
        }

        setStraightHours(st);
        setOvertimeHours(ot);
      }
    }, [sessionEstimate]);

    /** Render **/

    const HoursDisplay = ({ hours }: { hours: number }) => (
      <Text size="small" weight={600} color="accent-1">
        {hours} hr{(hours ?? 0) > 1 && 's'}
      </Text>
    );

    return (
      <Box
        round={borderRadiusSize}
        flex="grow"
        elevation="small"
        background="light-1"
        animation={{ type: 'fadeIn', duration: 500 }}
        // border={upperSideBorders}
      >
        {/* Header */}
        <Box
          direction="row"
          gap="small"
          background={{ color: 'brand-gradient', dark: true }}
          style={{ background: 'var(--brand-gradient)', zIndex: 1 }}
          height={{ max: '80px', min: '80px' }}
          round={{ corner: 'top', size: borderRadiusSize }}
          pad={{ horizontal: 'small' }}
          align="center"
        >
          <Box direction="row" gap="small" flex="grow" align="center">
            <Box width="small" direction="row" align="center">
              <DateInput
                color="accent-1"
                value={session && new Date(`${session.date}T00:00:00`).toISOString()}
                format="mm/dd/yyyy"
                calendarProps={{ disabled: sessionStore.getSessionDates(sessionId) }}
                onChange={(event) => session && typeof event.value === 'string' && updateSession({ date: event.value })}
              />
            </Box>
            <Box direction="row" border={{ side: 'bottom', size: '0.5px', color: 'accent-1' }}>
              <Select
                value={session?.commercial}
                onChange={({ value }) => updateSession({ commercialId: value.id })}
                placeholder="Choose commercial..."
                options={commercials}
                plain
                width="small"
                valueKey="id"
                labelKey="title"
              />
            </Box>
            <Box
              direction="row"
              align="center"
              gap="xsmall"
              onClick={() => session && updateSession({ isUnion: !session.is_union })}
            >
              <CheckBox checked={session?.is_union} />
              <Text size="small" color="accent-1">
                Union Session
              </Text>
            </Box>
          </Box>

          <Box direction="row" gap="xsmall" flex="grow" pad="medium" round={borderRadiusSize}>
            <Box>
              <Box align="end" justify="end" flex="grow">
                {!!straightHours && (
                  <Text size="xsmall" color="accent-1">
                    STRAIGHT TIME:
                  </Text>
                )}
              </Box>
              <Box align="end" justify="end" flex="grow">
                {!!overtimeHours && (
                  <Text size="xsmall" color="accent-1">
                    OVERTIME:
                  </Text>
                )}
              </Box>
            </Box>
            <Box>
              <Box align="start" justify="end" flex="grow">
                {!!straightHours && <HoursDisplay hours={straightHours} />}
              </Box>
              <Box align="start" justify="end" flex="grow">
                {!!overtimeHours && <HoursDisplay hours={overtimeHours} />}
              </Box>
            </Box>
          </Box>

          <Box align="end" width="10%">
            <Box direction="column" gap="xxsmall" justify="end" align="end" flex="grow">
              <Text size="xsmall" weight={500} color="accent-1">
                SESSION WAGES
              </Text>
              {sessionEstimate && (
                <Text size="1.15rem" weight="bold" color="accent-1">
                  {USD(sessionEstimate?.subtotal)}
                </Text>
              )}
            </Box>
          </Box>

          <Box align="end" width="10%">
            <Box direction="column" gap="xxsmall" justify="end" align="end" flex="grow">
              <Text size="small" weight={500} color="accent-1">
                SESSION TOTAL
              </Text>
              {sessionEstimate && (
                <Text size="1.15rem" weight="bold" color="accent-1">
                  {USD(sessionEstimate?.subtotal_plus_fringe)}
                </Text>
              )}
            </Box>
          </Box>

          <Box width="48px" height="48px" align="center" justify="center">
            <Delete onClick={() => onDelete()} name={dayjs(session?.date).format('MM/DD/YYYY')} />
          </Box>
        </Box>

        {/* Body */}
        <Box
          animation={{ type: 'slideDown', size: 'medium' }}
          flex="grow"
          align={!session ? 'center' : undefined}
          pad={!session ? 'medium' : undefined}
          background="accent-1"
          // round={{ corner: 'bottom', size: borderRadiusSize }}
          elevation="xsmall"
        >
          {!session && <Spinner size="small" />}
          {session?.activities && session?.activities.length > 0 && (
            <>
              {/* Table Header */}

              <Box
                direction="row"
                justify="start"
                align="center"
                gap="small"
                // style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                background="light-2"
                animation={{ type: 'slideDown', size: 'medium', delay: 300 }}
                elevation="xsmall"
              >
                <Box width="6%">
                  <Text size="xsmall">HOURS</Text>
                </Box>
                <Box width="16%">
                  <Text size="xsmall">ROLES</Text>
                </Box>
                <Box width="20%">
                  <Text size="xsmall">ACTIVITY TYPE</Text>
                </Box>
                <Box width="16%">
                  <Text size="xsmall">LOCATION</Text>
                </Box>
                {/* <Box flex /> */}
                <Box align="start" flex="grow">
                  <Text size="xsmall">HOUR TOTALS</Text>
                </Box>
                <Box align="end" width="10%">
                  <Text size="xsmall" weight={600}>
                    WAGES
                  </Text>
                </Box>
                <Box align="end" width="10%">
                  <Text size="xsmall" weight={600}>
                    TOTAL
                  </Text>
                </Box>

                <Box width="48px" />
              </Box>

              {/* Table Rows */}
              <Box direction="column" gap="small" animation={{ type: 'slideDown', size: 'medium' }}>
                {session.activities
                  .sort((a, b) => (dayjs(a.created_at).isAfter(b.created_at) ? 1 : -1))
                  .map((activity, i) => (
                    <SessionActivityRow
                      key={activity.id}
                      rowIndex={i}
                      script={session.script}
                      session={session}
                      roles={roles}
                      locations={locations}
                      deleteSessionActivity={deleteSessionActivity}
                      activity={activity}
                      updateSessionActivity={(update) => updateSessionActivity({ ...activity, ...update })}
                    />
                  ))}
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          gap="small"
          // flex="grow"
          background="light-2"
          round={{ corner: 'bottom', size: borderRadiusSize }}
          // border={{ side: 'bottom', size: '1px' }}
          direction="row"
          justify="end"
          pad="xsmall"
        >
          <AddNewButton
            icon={
              <TalonTip content={<Text>Add An Activity</Text>}>
                <Yoga />
              </TalonTip>
            }
            onClick={() => addSessionActivity()}
          />
        </Box>
      </Box>
    );
  }
);
